"use strict";

import paginate from 'jw-paginate';
import {splitDatetime} from './utils.js';

class List {
	constructor(itemsElement)
	{
		this.items = itemsElement;
		this.quickSearch = document.querySelector('.js-quick-search');
		this.sortPicker = document.querySelector('.js-sort-picker');
		this.orderPicker = document.querySelector('.js-order-picker');
		this.filterBox = document.querySelector('.js-filter');
		this.addConditionBtn = document.querySelector('.js-add-condition-btn');
		this.statusBox = document.querySelector('.js-status');
		this.pinsEditable = JSON.parse(this.items.dataset.pinsEditable);

		this.addLimitPicker();
		this.applySettings();
		this.loadPinnedConditions();
		this.initTableWithInput();

		this.quickSearch.addEventListener("change", () => this.initTableWithInput());
		this.quickSearch.addEventListener("keyup", () => this.initTableWithInput());

		this.sortPicker.addEventListener("change", () => this.updateTableWithInput());
		this.orderPicker.addEventListener("change", () => this.updateTableWithInput());

		this.addConditionBtn.addEventListener("click", () => this.addFilterCondition());

		window.addEventListener("unload", () => this.storeSettings());
	}

	defaultSettings()
	{
		return {
			quick: "",
			page: 1,
			limit: 20,
			sort: this.sortPicker.value,
			order: "DESC",
			filter: [],
		};
	}

	settings()
	{
		let ds = this.defaultSettings();
		let s = JSON.parse(sessionStorage.getItem(this.items.dataset.storageKey));
		for (let k in s) {
			ds[k] = s[k];
		}
		return ds;
	}

	addFilterCondition(field, operator, expression, isPinned)
	{
		isPinned = typeof isPinned !== 'undefined' ? isPinned : false;

		let fieldDefinitions = JSON.parse(this.items.dataset.fieldDefinitions);
		let wrapper = document.createElement('div');
		wrapper.classList.add("input-group", "js-filter-condition");
		wrapper.dataset.isPinned = isPinned ? 1 : 0;

		let picker = document.createElement('select');
		picker.classList.add("form-control", "js-field-picker");
		picker.disabled = isPinned;

		for (let fieldName in fieldDefinitions) {
			if (fieldDefinitions.hasOwnProperty(fieldName)) {
				let option = new Option(fieldDefinitions[fieldName].title, fieldName);
				picker.appendChild(option);
			}
		}

		if (typeof field !== 'undefined') {
			picker.value = field;
		}

		picker.addEventListener("change", () => this.updateConditionOperator(wrapper));
		wrapper.appendChild(picker);
		this.addConditionBtn.parentNode.insertBefore(wrapper, this.addConditionBtn);
		this.updateConditionOperator(wrapper, operator, expression);
	}

	updateConditionOperator(wrapper, operator, expression)
	{
		let fieldPicker = wrapper.querySelector('.js-field-picker');
		let field = fieldPicker.value;
		let fieldDefinitions = JSON.parse(this.items.dataset.fieldDefinitions);
		let fieldType = fieldDefinitions[field].type;
		let operators = JSON.parse(this.items.dataset.operatorTitles)[fieldType];
		let operatorPicker = document.createElement("select");
		operatorPicker.classList.add("form-control", "js-operator-picker");
		operatorPicker.disabled = wrapper.dataset.isPinned == 1;

		for (let operatorKey in operators) {
			if (operators.hasOwnProperty(operatorKey)) {
				let option = new Option(operators[operatorKey], operatorKey);
				operatorPicker.appendChild(option);
			}
		}

		let siblings = Array.prototype.filter.call(fieldPicker.parentNode.children, sibling => {
			return sibling !== fieldPicker;
		});
		siblings.forEach(sibling => sibling.remove());

		if (typeof operator !== 'undefined') {
			operatorPicker.value = operator;
		}

		operatorPicker.addEventListener("change", () => {
			let lastExpression = this.getConditionExpression(wrapper);

			// TODO: better check
			let expressionElements = wrapper.querySelectorAll("[class*=js-expression]");
			expressionElements.forEach(element => element.remove());

			this.updateConditionExpression(field, operatorPicker, lastExpression);
			this.initTableWithInput();
		});
		wrapper.appendChild(operatorPicker);

		this.updateConditionExpression(field, operatorPicker, expression);

		let pinButton = document.createElement("button");
		pinButton.type = "button";
		pinButton.classList.add("btn", "btn-sm", "btn-outline-info");
		pinButton.innerHTML = `<i class="fas fa-thumbtack"></i>`;
		pinButton.disabled = operatorPicker.disabled;

		let deleteButton = document.createElement("button");
		deleteButton.type = "button";
		deleteButton.classList.add("btn", "btn-sm", "btn-outline-danger");
		deleteButton.innerHTML = `<i class="fas fa-times"></i>`;

		pinButton.addEventListener("click", () => {
			wrapper.dataset.isPinned = 1;
			pinButton.innerHTML = `<div class="spinner-border spinner-border-sm ml-auto" role="status" aria-hidden="true"></div>`;
			pinButton.disabled = true;
			deleteButton.disabled = true;

			wrapper.querySelector(".js-field-picker").disabled = true;
			operatorPicker.disabled = true;
			wrapper.querySelectorAll("[class*=js-expression]").forEach(element => element.disabled = true);

			// Abort previous XHR
			if (this.pinnedXhr && this.pinnedXhr.readyState != 4) {
				this.pinnedXhr.abort();
			}

			this.pinnedXhr = new XMLHttpRequest();
			this.pinnedXhr.onload = () => {
				// TODO: handle error in this.pinnedXhr.responseText
				pinButton.innerHTML = `<i class="fas fa-thumbtack"></i>`;
				deleteButton.disabled = false;
			}
			this.pinnedXhr.open("POST", this.items.dataset.pinnedConditionsUrl);
			this.pinnedXhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
			this.pinnedXhr.setRequestHeader('Content-Type', 'application/json');
			this.pinnedXhr.send(JSON.stringify({
				conditions: this.getFilter(true, false),
			}));
		});

		if (wrapper.dataset.isPinned == 1 || this.pinsEditable) {
			wrapper.appendChild(pinButton);
		}

		deleteButton.addEventListener("click", () => {
			if (wrapper.dataset.isPinned == 1) {
				wrapper.dataset.isPinned = 0;
				deleteButton.innerHTML = `<div class="spinner-border spinner-border-sm ml-auto" role="status" aria-hidden="true"></div>`;
				deleteButton.disabled = true;

				// Abort previous XHR
				if (this.pinnedXhr && this.pinnedXhr.readyState != 4) {
					this.pinnedXhr.abort();
				}

				this.pinnedXhr = new XMLHttpRequest();
				this.pinnedXhr.onload = () => {
					// TODO: handle error in this.pinnedXhr.responseText
					wrapper.remove();
					this.initTableWithInput();
				}
				this.pinnedXhr.open("POST", this.items.dataset.pinnedConditionsUrl);
				this.pinnedXhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
				this.pinnedXhr.setRequestHeader('Content-Type', 'application/json');
				this.pinnedXhr.send(JSON.stringify({
					conditions: this.getFilter(true, false),
				}));
			} else {
				wrapper.remove();
				this.initTableWithInput();
			}
		});

		if (wrapper.dataset.isPinned == 0 || this.pinsEditable) {
			wrapper.appendChild(deleteButton);
		}
	}

	updateConditionExpression(field, operatorPicker, expression)
	{
		let operator = operatorPicker.value;
		let fieldDefinitions = JSON.parse(this.items.dataset.fieldDefinitions);

		if (["dt-lt", "dt-gt"].includes(operator)) {
			let expressionDateInput = document.createElement("input");
			expressionDateInput.type = "date";
			expressionDateInput.classList.add("form-control", "js-expression-date-input");
			expressionDateInput.disabled = operatorPicker.disabled;

			let expressionTimeInput = document.createElement("input");
			expressionTimeInput.type = "time";
			expressionTimeInput.classList.add("form-control", "js-expression-time-input");
			expressionTimeInput.disabled = operatorPicker.disabled;

			if (typeof expression !== 'undefined') {
				let splitDt = splitDatetime(expression);
				expressionDateInput.value = splitDt.date.trim();
				expressionTimeInput.value = splitDt.time.trim();
			}

			expressionDateInput.addEventListener("change", () => this.initTableWithInput());
			operatorPicker.after(expressionDateInput);
			expressionTimeInput.addEventListener("change", () => this.initTableWithInput());
			expressionDateInput.after(expressionTimeInput);
		} else if (["set-equals", "set-ne", "in", "notin"].includes(operator)) {
			let expressionSelect = document.createElement("select");
			expressionSelect.classList.add("form-control", "js-expression-select");
			expressionSelect.disabled = operatorPicker.disabled;

			if (["in", "notin"].includes(operator)) {
				expressionSelect.multiple = true;
				expressionSelect.size = 7;
			}

			let fieldValues = fieldDefinitions[field].values;
			let selected = ["-1"];

			if (typeof expression === 'string') {
				selected = [expression];
			} else if (Array.isArray(expression)) {
				selected = expression;
			}

			for (let key in fieldValues) {
				if (fieldValues.hasOwnProperty(key)) {
					if (typeof fieldValues[key] === "string") {
						expressionSelect.appendChild(new Option(
							fieldValues[key],
							key,
							false,
							selected.includes(key)
						));
					} else {
						let optgroup = document.createElement("optgroup");
						optgroup.label = fieldValues[key].label;

						for (let value in fieldValues[key].options) {
							if (fieldValues[key].options.hasOwnProperty(value)) {
								optgroup.appendChild(new Option(
									fieldValues[key].options[value],
									value,
									false,
									selected.includes(value)
								));
							}
						}

						expressionSelect.appendChild(optgroup);
					}
				}
			}

			expressionSelect.addEventListener("change", () => this.initTableWithInput());
			operatorPicker.after(expressionSelect);
			// TODO: trigger operatorPicker "change" event
		} else {
			let expressionInput = document.createElement("input");
			expressionInput.type = "text";
			expressionInput.classList.add("form-control", "js-expression-input");
			expressionInput.disabled = operatorPicker.disabled;

			if (typeof expression !== 'undefined') {
				expressionInput.value = expression;
			}

			expressionInput.addEventListener("keyup", () => this.initTableWithInput());
			operatorPicker.after(expressionInput);
		}
	}

	addLimitPicker()
	{
		let label = document.createElement("label");
		label.htmlFor = "limit-picker";
		label.innerText = this.items.dataset.limitPickerLabel;
		this.limitPicker = document.createElement("select");
		this.limitPicker.id = "limit-picker";
		this.limitPicker.classList.add("form-control", "js-limit-picker");
		let limits = [10, 20, 50, 100, 200];
		for (let i in limits) {
			this.limitPicker.appendChild(new Option(limits[i], limits[i]));
		}
		this.items.parentNode.insertBefore(this.limitPicker, this.items.nextSibling);
		this.items.parentNode.insertBefore(label, this.items.nextSibling);
		this.limitPicker.addEventListener("change", () => this.initTableWithInput());
	}

	updatePaginator(totalItems, currentPage, pageSize)
	{
		let pagination = paginate(totalItems, currentPage, pageSize, 7);
		let totalPages = pagination.totalPages;
		let startPage = pagination.startPage;
		let endPage = pagination.endPage;

		this.statusBox.innerHTML = this.items.dataset.recordCountLabel.replace("%1", totalItems);

		this.pagePicker = document.createElement("select");
		this.pagePicker.id = "page-picker";
		this.pagePicker.classList.add("form-control", "js-page-picker");
		for (let i = 1; i <= totalPages; i++) {
			this.pagePicker.appendChild(new Option(i, i));
		}
		this.pagePicker.value = currentPage;
		this.pagePicker.addEventListener("change", () => this.updateTableWithInput());

		let ul = document.createElement("ul");
		ul.classList.add("pagination");

		{
			let li = document.createElement("li");
			li.classList.add("page-item");
			let icon = document.createElement("span");
			icon.setAttribute("aria-hidden", "true");
			icon.innerHTML = "&laquo;";
			if (currentPage > 1) {
				let a = document.createElement("a");
				a.classList.add("page-link");
				a.href = "#";
				a.setAttribute("aria-label", "Previous");
				a.addEventListener("click", e => {
					e.preventDefault();
					this.pagePicker.value = currentPage - 1;
					this.pagePicker.dispatchEvent(new Event('change'));
				});
				a.appendChild(icon);
				li.appendChild(a);
			} else {
				li.classList.add("disabled");
				let span = document.createElement("span");
				span.classList.add("page-link");
				span.appendChild(icon);
				li.appendChild(span);
			}
			ul.appendChild(li);
		}

		for (let i = startPage; i <= endPage; i++) {
			let li = document.createElement("li");
			li.classList.add("page-item");
			if (i == currentPage) {
				li.classList.add("active");
				li.setAttribute("aria-current", "page");
				let span = document.createElement("span");
				span.classList.add("page-link");
				span.innerHTML = `${i} <span class="sr-only">(current)</span>`
				li.appendChild(span);
			} else {
				let a = document.createElement("a");
				a.classList.add("page-link");
				a.href = "#";
				a.innerText = i;
				a.addEventListener("click", e => {
					e.preventDefault();
					this.pagePicker.value = i;
					this.pagePicker.dispatchEvent(new Event('change'));
				});
				li.appendChild(a);
			}
			ul.appendChild(li);
		}

		{
			let li = document.createElement("li");
			li.classList.add("page-item");
			let icon = document.createElement("span");
			icon.setAttribute("aria-hidden", "true");
			icon.innerHTML = "&raquo;";
			if (currentPage < totalPages) {
				let a = document.createElement("a");
				a.classList.add("page-link");
				a.href = "#";
				a.setAttribute("aria-label", "Next");
				a.addEventListener("click", e => {
					e.preventDefault();
					this.pagePicker.value = currentPage + 1;
					this.pagePicker.dispatchEvent(new Event('change'));
				});
				a.appendChild(icon);
				li.appendChild(a);
			} else {
				li.classList.add("disabled");
				let span = document.createElement("span");
				span.classList.add("page-link");
				span.appendChild(icon);
				li.appendChild(span);
			}
			ul.appendChild(li);
		}

		let nav = document.querySelector('.js-pagination');
		nav.innerHTML = "";
		nav.appendChild(ul);
		let label = document.createElement("label");
		label.htmlFor = "page-picker";
		label.innerText = this.items.dataset.pagePickerLabel;
		nav.appendChild(label);
		nav.appendChild(this.pagePicker)
	}

	getFilter(withPinned, withNormal)
	{
		withPinned = typeof withPinned !== 'undefined' ? withPinned : true;
		withNormal = typeof withNormal !== 'undefined' ? withNormal : true;

		let filter = [];
		let conditions = this.filterBox.querySelectorAll('.js-filter-condition');
		for (let i = 0; i < conditions.length; i++) {
			let condition = conditions[i];

			if ((condition.dataset.isPinned == 1 && withPinned) || (condition.dataset.isPinned == 0 && withNormal)) {
				filter.push({
					field: condition.querySelector('.js-field-picker').value,
					operator: condition.querySelector('.js-operator-picker').value,
					expression: this.getConditionExpression(condition),
				});
			}
		}
		return filter;
	}

	getConditionExpression(condition)
	{
		let expression = "";
		let expressionInput = condition.querySelector('.js-expression-input');

		if (expressionInput) {
			expression = expressionInput.value;
		} else if (condition.querySelector('.js-expression-date-input')) {
			let expressionDateInput = condition.querySelector('.js-expression-date-input');
			let expressionTimeInput = condition.querySelector('.js-expression-time-input');
			let timeValue = expressionTimeInput.value;
			timeValue = timeValue.trim().replace(/:/g, "").padEnd(6, "0");
			timeValue = timeValue.slice(0, 2) + ":" + timeValue.slice(2, 4) + ":" + timeValue.slice(4, 6);

			let timezone = "-";
			let timezoneOffsetInMinutes = (new Date()).getTimezoneOffset();
			if (timezoneOffsetInMinutes <= 0) {
				timezone = "+";
				timezoneOffsetInMinutes = -timezoneOffsetInMinutes;
			}
			let timezoneOffsetHours = Math.floor(timezoneOffsetInMinutes / 60);
			let timezoneOffsetMinutes = timezoneOffsetInMinutes % 60;
			timezone += timezoneOffsetHours.toString().padStart(2, "0") + ":" + timezoneOffsetMinutes.toString().padStart(2, "0")

			expression = expressionDateInput.value + "T" + timeValue + timezone;
		} else if (condition.querySelector('.js-expression-select')) {
			let expressionSelect = condition.querySelector('.js-expression-select');
			expression = expressionSelect.multiple
				? [...expressionSelect.selectedOptions].map(option => option.value)
				: expressionSelect.value;
		}

		return expression;
	}

	afterDataLoaded(data)
	{
	}

	applySettings()
	{
		let s = this.settings();
		this.quickSearch.value = s["quick"];
		this.limitPicker.value = s["limit"];
		this.sortPicker.value = s["sort"];
		this.orderPicker.value = s["order"];

		let siblings = Array.prototype.filter.call(this.addConditionBtn.parentNode.children, sibling => {
			return sibling !== this.addConditionBtn;
		});
		siblings.forEach(sibling => sibling.remove());

		for (let i = 0; i < s["filter"].length; i++) {
			let condition = s["filter"][i];
			this.addFilterCondition(condition.field, condition.operator, condition.expression);
		}
	}

	loadPinnedConditions()
	{
		let pc = JSON.parse(this.items.dataset.pinnedConditions);

		for (let i = 0; i < pc.length; i++) {
			let condition = pc[i];
			this.addFilterCondition(condition.field, condition.operator, condition.expression, true);
		}
	}

	storeSettings()
	{
		let ds = this.defaultSettings();
		ds["quick"] = this.quickSearch.value;
		ds["limit"] = this.limitPicker.value;
		ds["sort"] = this.sortPicker.value;
		ds["order"] = this.orderPicker.value;
		ds["filter"] = this.getFilter(false);
		sessionStorage.setItem(this.items.dataset.storageKey, JSON.stringify(ds));
	}

	updateTable(quick, page, limit, sort, order, filter)
	{
		// Abort previous XHR
		if (this.xhr && this.xhr.readyState != 4) {
			this.xhr.abort();
		}

		this.xhr = new XMLHttpRequest();
		this.xhr.onload = () => {
			let data = JSON.parse(this.xhr.responseText);
			let totalPages = Math.ceil(data["totalItems"] / limit);
			this.items.querySelector('tbody').innerHTML = data["html"];

			this.updatePaginator(data["totalItems"], page, limit);
			this.afterDataLoaded(data);
		}
		this.xhr.open("POST", this.items.dataset.searchUrl);
		this.xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
		//this.xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
		//this.xhr.send($.param(postData));
		this.xhr.setRequestHeader('Content-Type', 'application/json');
		this.xhr.send(JSON.stringify({
			quick: quick,
			page: page,
			limit: limit,
			sort: sort,
			order: order,
			filter: filter,
		}));

		this.statusBox.innerHTML = `<div class="spinner-border spinner-border-sm ml-auto" role="status" aria-hidden="true">
	<span class="sr-only">Loading&hellip;</span>
</div>`;
	}

	updateTableWithInput()
	{
		this.updateTable(
			this.quickSearch.value,
			parseInt(this.pagePicker.value),
			parseInt(this.limitPicker.value),
			this.sortPicker.value,
			this.orderPicker.value,
			this.getFilter()
		);
	}

	initTableWithInput()
	{
		this.updateTable(
			this.quickSearch.value,
			1,
			parseInt(this.limitPicker.value),
			this.sortPicker.value,
			this.orderPicker.value,
			this.getFilter()
		);
	}
}

export {List};
